import {
  type TextFieldProps,
  TextField as BaseTextField,
  FormHelperText,
  Stack,
} from "@mui/material";
import { Box } from "@mui/system";
import {
  type ChangeEvent,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
  type KeyboardEvent,
  type MouseEvent,
  type ClipboardEvent,
  useRef,
  Fragment,
  useLayoutEffect,
} from "react";

interface IProps extends Pick<
  TextFieldProps,
  "name" | "size" | "error" | "helperText" | "inputProps"
> {
  separator: ReactNode;
  length: number;
  value: string;
  onlyNumbers?: boolean;
  onChange: Dispatch<SetStateAction<string>>;
}

const OTPInput = ({
  name,
  separator,
  length,
  value,
  error,
  helperText,
  inputProps,
  onlyNumbers,
  size = "small",
  onChange,
}: IProps) => {
  const isSmallSize = size === "small";

  const inputRefs = useRef<HTMLInputElement[]>(new Array(length).fill(null));

  const focusInput = (targetIndex: number) => {
    const targetInput = inputRefs.current[targetIndex];
    targetInput.focus();
  };

  const selectInput = (targetIndex: number) => {
    const targetInput = inputRefs.current[targetIndex];
    targetInput?.select();
  };

  const handleKeyDown = (
    event: KeyboardEvent<HTMLDivElement>,
    currentIndex: number,
  ) => {
    switch (event.key) {
      case "ArrowUp":
      case "ArrowDown":
      case " ":
        event.preventDefault();
        break;
      case "ArrowLeft":
        event.preventDefault();
        if (currentIndex > 0) {
          focusInput(currentIndex - 1);
          selectInput(currentIndex - 1);
        }
        break;
      case "ArrowRight":
        event.preventDefault();
        if (currentIndex < length - 1) {
          focusInput(currentIndex + 1);
          selectInput(currentIndex + 1);
        }
        break;
      case "Delete":
        event.preventDefault();
        onChange((prevOtp) => {
          const otp =
            prevOtp.slice(0, currentIndex) + prevOtp.slice(currentIndex + 1);
          return otp;
        });

        break;
      case "Backspace":
        event.preventDefault();
        if (currentIndex > 0) {
          focusInput(currentIndex - 1);
          selectInput(currentIndex - 1);
        }

        onChange((prevOtp) => {
          const otp =
            prevOtp.slice(0, currentIndex) + prevOtp.slice(currentIndex + 1);
          return otp;
        });
        break;

      default:
        break;
    }
  };

  const handleChange = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    currentIndex: number,
  ) => {
    const currentValue = event.target.value;
    const finalValue = onlyNumbers
      ? currentValue.replace(/\D/g, "")
      : currentValue;

    let indexToEnter = 0;

    while (indexToEnter <= currentIndex) {
      if (
        inputRefs.current[indexToEnter].value &&
        indexToEnter < currentIndex
      ) {
        indexToEnter += 1;
      } else {
        break;
      }
    }

    onChange((prev) => {
      const otpArray = prev.split("");
      const lastValue = finalValue[finalValue.length - 1];

      otpArray[indexToEnter] = lastValue;
      return otpArray.join("");
    });

    if (finalValue !== "") {
      if (currentIndex < length - 1) {
        focusInput(currentIndex + 1);
      }
    }
  };

  const handleClick = (
    event: MouseEvent<HTMLDivElement>,
    currentIndex: number,
  ) => {
    selectInput(currentIndex);
  };

  const handlePaste = (
    event: ClipboardEvent<HTMLDivElement>,
    currentIndex: number,
  ) => {
    event.preventDefault();
    const clipboardData = event.clipboardData;

    // Check if there is text data in the clipboard
    if (clipboardData.types.includes("text/plain")) {
      let pastedText = clipboardData.getData("text/plain");
      pastedText = pastedText.substring(0, length).trim();
      let indexToEnter = 0;

      while (indexToEnter <= currentIndex) {
        if (
          inputRefs.current[indexToEnter].value &&
          indexToEnter < currentIndex
        ) {
          indexToEnter += 1;
        } else {
          break;
        }
      }

      const otpArray = value.split("");

      for (let i = indexToEnter; i < length; i += 1) {
        const lastValue = pastedText[i - indexToEnter] ?? " ";
        otpArray[i] = lastValue;
      }

      onChange(otpArray.join(""));
    }
  };

  useLayoutEffect(() => {
    console.log("1");

    focusInput(0);
  }, []);

  return (
    <Stack>
      <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
        {new Array(length).fill(null).map((_, index) => (
          <Fragment key={index}>
            <BaseTextField
              name={name}
              inputProps={{
                style: {
                  padding: isSmallSize ? 8 : 10,
                  textAlign: "center",
                },
                ...inputProps,
              }}
              size={size}
              inputRef={(ref) => {
                inputRefs.current[index] = ref;
              }}
              value={value[index] ?? ""}
              onChange={(event) => handleChange(event, index)}
              onKeyDown={(event) => handleKeyDown(event, index)}
              onClick={(event) => handleClick(event, index)}
              onPaste={(event) => handlePaste(event, index)}
              error={error}
              autoComplete="off"
            />
            {index === length - 1 ? null : separator}
          </Fragment>
        ))}
      </Box>

      {/* We render the errors manually because OTPInput consists of 6 tiny inputs,
      and RHF does not work with that setup */}
      {helperText && (
        <FormHelperText error variant="outlined" sx={{ ml: 0.5, mt: 1 }}>
          {helperText}
        </FormHelperText>
      )}
    </Stack>
  );
};

OTPInput.displayName = "OTPInput";

export default OTPInput;
